import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';

import { FormInput, type FormInputProps } from './form-input';

export type ControlledInputProps<T extends FieldValues> = Omit<
  FormInputProps,
  'value' | 'onChangeText' | 'onBlur' | 'status' | 'errorMessage'
> & {
  control: Control<T>;
  name: FieldPath<T>;
  /** Sanitize the raw text before it's stored (e.g. digits-only for a zip code). */
  sanitize?: (text: string) => string;
};

/**
 * `FormInput` pre-wired to a react-hook-form field. Pass `control` + `name` instead of
 * repeating a `<Controller>` with value/onChange/onBlur/status/errorMessage at every call site.
 */
export function ControlledInput<T extends FieldValues>({
  control,
  name,
  sanitize,
  ...rest
}: ControlledInputProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
        <FormInput
          {...rest}
          value={(value as string | null | undefined) ?? ''}
          onChangeText={text => onChange(sanitize ? sanitize(text) : text)}
          onBlur={onBlur}
          status={error?.message ? 'error' : 'default'}
          errorMessage={error?.message}
        />
      )}
    />
  );
}

export default ControlledInput;
